import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Shield, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  CreditCard,
  LogOut,
  Loader2,
  ArrowRight,
  Settings,
  Megaphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, isAuthenticated, loading, logout, getAuthHeaders } = useAdminAuth();
  
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/admin/login");
    }
  }, [loading, isAuthenticated, navigate]);

  // Load dashboard stats
  useEffect(() => {
    const loadStats = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await axios.get(`${API}/dashboard`, {
          headers: getAuthHeaders(),
        });
        setStats(response.data);
      } catch (error) {
        toast.error("Erro ao carregar estatísticas");
      } finally {
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [isAuthenticated, getAuthHeaders]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  const statCards = [
    {
      label: "Total de Usuários",
      value: stats?.total_users || 0,
      icon: Users,
      color: "bg-indigo-50 text-indigo-600 border-indigo-200",
    },
    {
      label: "Em Trial",
      value: stats?.users_trial || 0,
      icon: Clock,
      color: "bg-amber-50 text-amber-600 border-amber-200",
    },
    {
      label: "Ativos",
      value: stats?.users_active || 0,
      icon: CheckCircle,
      color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    },
    {
      label: "Expirados",
      value: stats?.users_expired || 0,
      icon: XCircle,
      color: "bg-rose-50 text-rose-600 border-rose-200",
    },
    {
      label: "Aguardando Aprovação",
      value: stats?.users_awaiting || 0,
      icon: AlertTriangle,
      color: "bg-blue-50 text-blue-600 border-blue-200",
    },
    {
      label: "Pagamentos Pendentes",
      value: stats?.pending_payments || 0,
      icon: CreditCard,
      color: "bg-purple-50 text-purple-600 border-purple-200",
      link: "/admin/pagamentos",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-gray-900" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-gray-900">
                Painel Admin
              </h1>
              <p className="text-xs text-gray-500">{admin?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/admin/usuarios">
              <Button variant="ghost" size="sm" className="gap-2 text-gray-600 hover:text-gray-900">
                <Users className="w-4 h-4" />
                Usuarios
              </Button>
            </Link>
            <Link to="/admin/pagamentos">
              <Button variant="ghost" size="sm" className="gap-2 text-gray-600 hover:text-gray-900">
                <CreditCard className="w-4 h-4" />
                Pagamentos
              </Button>
            </Link>
            <Link to="/admin/configuracoes">
              <Button variant="ghost" size="sm" className="gap-2 text-gray-600 hover:text-gray-900">
                <Settings className="w-4 h-4" />
                Configurações
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="gap-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="animate-fade-in-up">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
            Dashboard
          </h2>

          {loadingStats ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {statCards.map((card, index) => (
                <div
                  key={card.label}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {card.link ? (
                    <Link to={card.link}>
                      <div className={`p-6 rounded-xl border ${card.color} hover:bg-gray-100 transition-colors cursor-pointer`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{card.label}</p>
                            <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <card.icon className="w-8 h-8" />
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className={`p-6 rounded-xl border ${card.color}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{card.label}</p>
                          <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                        </div>
                        <card.icon className="w-8 h-8" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-8">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
              Ações Rápidas
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/usuarios">
                <Button 
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="manage-users-button"
                >
                  <Users className="w-4 h-4" />
                  Gerenciar Usuarios
                </Button>
              </Link>
              <Link to="/admin/pagamentos">
                <Button 
                  className="gap-2 bg-rose-600 hover:bg-rose-700"
                  data-testid="view-payments-button"
                >
                  <CreditCard className="w-4 h-4" />
                  Ver Pagamentos Pendentes
                  {stats?.pending_payments > 0 && (
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-rose-100 text-rose-600">
                      {stats.pending_payments}
                    </span>
                  )}
                </Button>
              </Link>
              <Link to="/admin/configuracoes">
                <Button 
                  variant="ghost"
                  className="gap-2 border border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  data-testid="view-config-button"
                >
                  <Settings className="w-4 h-4" />
                  Configurar Planos
                </Button>
              </Link>
              <Link to="/admin/propagandas">
                <Button 
                  variant="ghost"
                  className="gap-2 border border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  data-testid="view-promotions-button"
                >
                  <Megaphone className="w-4 h-4" />
                  Propagandas
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
